import React, { useState, useRef, useEffect, useCallback } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminMainText,
  AdminMetadataText,
  AdminSupportingText,
  AdminMetric,
  AdminAlertText,
  AdminHeaderAction,
  AdminFormSecondaryAction,
} from "../../sections/AdminDashboard/shared/adminUx";
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from "../BusinessDashboard/AdminWidgetChrome";
import { useGoogleMaps } from "../../context/GoogleMapsProvider";

const LocalTradesTrendingServicesWidget = () => {
  const navigate = useNavigate();
  const { isLoaded, loadError } = useGoogleMaps();
  const [mapFilterPeriod, setMapFilterPeriod] = useState("7 Days");
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [mapCenter] = useState({ lat: -37.8136, lng: 144.9631 });
  const [mapZoom] = useState(10);
  const mapRef = useRef(null);

  // Dynamic trending services data for different periods
  const trendingServicesByPeriod = {
    '7 Days': [
      { name: "Plumbing", change: 32, trend: "up", avgViews: 186 },
      { name: "Electrical", change: 18, trend: "up", avgViews: 142 },
      { name: "Cleaning", change: 12, trend: "up", avgViews: 103 },
      { name: "Roof Repairs", change: 7, trend: "up", avgViews: 76 }
    ],
    '14 Days': [
      { name: "Plumbing", change: 28, trend: "up", avgViews: 245 },
      { name: "Electrical", change: 22, trend: "up", avgViews: 198 },
      { name: "Painting", change: 15, trend: "up", avgViews: 167 },
      { name: "Cleaning", change: 14, trend: "up", avgViews: 145 }
    ],
    '30 Days': [
      { name: "Electrical", change: 35, trend: "up", avgViews: 312 },
      { name: "Plumbing", change: 31, trend: "up", avgViews: 289 },
      { name: "Landscaping", change: 19, trend: "up", avgViews: 221 },
      { name: "Painting", change: 17, trend: "up", avgViews: 198 }
    ],
    '90 Days': [
      { name: "Plumbing", change: 42, trend: "up", avgViews: 456 },
      { name: "Electrical", change: 38, trend: "up", avgViews: 423 },
      { name: "HVAC", change: 29, trend: "up", avgViews: 345 },
      { name: "Roofing", change: 24, trend: "up", avgViews: 289 }
    ]
  };

  // Get current period data
  const trendingServices = trendingServicesByPeriod[mapFilterPeriod];

  // Calculate max views for bar scaling
  const maxViews = Math.max(...trendingServices.map(s => s.avgViews));

  // Get demand label based on growth percentage
  const getDemandLabel = (change) => {
    if (change > 25) return "High Demand";
    if (change >= 10) return "Moderate Demand";
    return "Low Demand";
  };

  // Sample trade service locations with x,y coordinates for custom pins
  const tradeLocations = [
    {
      id: 1,
      x: 30, y: 35,
      businessName: "TradePro",
      service: "Plumbing",
      location: "Werribee",
      rating: 4.9,
      isPro: true,
      reviews: 1247,
      profileImage: "https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1743126772423.jpg"
    },
    {
      id: 2,
      x: 50, y: 25,
      businessName: "Elite Electric",
      service: "Electrical",
      location: "Hoppers Crossing",
      rating: 4.7,
      isPro: true,
      reviews: 892,
      profileImage: "https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1743126772423.jpg"
    },
    {
      id: 3,
      x: 65, y: 45,
      businessName: "Clean Masters",
      service: "Cleaning",
      location: "Point Cook",
      rating: 4.8,
      isPro: false,
      reviews: 634,
      profileImage: "https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1743126772423.jpg"
    },
    {
      id: 4,
      x: 40, y: 60,
      businessName: "Garden Pro",
      service: "Landscaping",
      location: "North Melbourne VIC",
      rating: 4.6,
      isPro: true,
      reviews: 445,
      profileImage: "https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1743126772423.jpg"
    },
    {
      id: 5,
      x: 20, y: 50,
      businessName: "Roof Masters",
      service: "Roof Repairs",
      location: "Altona",
      rating: 4.5,
      isPro: false,
      reviews: 320,
      profileImage: "https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1743126772423.jpg"
    },
    {
      id: 6,
      x: 70, y: 30,
      businessName: "Paint Pros",
      service: "Painting",
      location: "Williamstown",
      rating: 4.8,
      isPro: true,
      reviews: 580,
      profileImage: "https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1743126772423.jpg"
    }
  ];

  // Set first pin as hovered by default
  useEffect(() => {
    if (tradeLocations.length > 0) {
      setHoveredMarker(tradeLocations[3]);
    }
  }, []);

  const handleViewProfile = (businessId) => {
    navigate(`/poster-profile/${businessId}`);
  };

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const focusOnMySuburb = useCallback(() => {
    if (mapRef.current) {
      setTimeout(() => {
        if (mapRef.current) {
          // Focus on Werribee suburb - zoom to 10km radius
          mapRef.current.panTo({ lat: -37.8136, lng: 144.9631 });
          mapRef.current.setZoom(12); // More zoomed in for 10km radius
        }
      }, 100);
    }
  }, []);

  const resetView = useCallback(() => {
    if (mapRef.current) {
      // Reset to default 50km view
      mapRef.current.panTo({ lat: -37.8136, lng: 144.9631 });
      mapRef.current.setZoom(10);
    }
  }, []);

  return (
    <DashboardMainContainer className="masonry-item w-[550px] h-[550px] flex flex-col overflow-hidden">
      <AdminWidgetHeader title="Local Map View">
        <AdminWidgetPeriodFilter period={mapFilterPeriod} onPeriodChange={setMapFilterPeriod} />
      </AdminWidgetHeader>

      <div className="flex-1 flex">
        {/* Left Section: Map */}
        <div className={`flex-1 relative ${process.env.NODE_ENV !== 'production' ? 'gm-dev-mask' : ''}`}>
          {process.env.NODE_ENV !== 'production' && (
            <style>{`
              .gm-dev-mask > div:nth-child(2) > div:nth-child(2) { display: none !important; }
            `}</style>
          )}
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={mapZoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                disableDefaultUI: true,
                mapTypeId: 'roadmap',
                styles: [
                  {
                    featureType: 'administrative',
                    elementType: 'geometry',
                    stylers: [{ visibility: 'simplified' }]
                  },
                  {
                    featureType: 'landscape',
                    stylers: [{ color: '#f8f9fa' }]
                  },
                  {
                    featureType: "all",
                    elementType: "labels.text.fill",
                    stylers: [{ color: "#464646" }]
                  },
                  {
                    featureType: "water",
                    elementType: "geometry.fill",
                    stylers: [{ color: "#e3f2fd" }]
                  }
                ]
              }}
            >
              {/* Custom Trade Pins */}
              {tradeLocations.map((location) => (
                <div
                  key={location.id}
                  className="absolute cursor-pointer group"
                  style={{ 
                    left: `${location.x}%`, 
                    top: `${location.y}%`,
                  }}
                  onMouseEnter={() => setHoveredMarker(location)}
                  onMouseLeave={() => setHoveredMarker(null)}
                >
                  {/* Pin Icon */}
                  <div className="relative">
                    <svg className="w-6 h-6 drop-shadow-lg" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#2E00FF"/>
                      <circle cx="12" cy="9" r="2.5" fill="white"/>
                    </svg>
                    
                    {/* Hover Popup */}
                    {hoveredMarker && hoveredMarker.id === location.id && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white rounded-xl shadow-xl p-4 min-w-[180px] max-w-[180px] z-20">
                        {/* Line 1: Business Name with Profile Image */}
                        <div className="flex items-center gap-2 mb-2">
                          <img 
                            src={location.profileImage} 
                            alt={location.businessName}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <AdminMainText className="text-offblack truncate block" title={location.businessName}>
                              {location.businessName}
                            </AdminMainText>
                            <AdminSupportingText className="text-mediumgray block">
                              {location.service}
                            </AdminSupportingText>
                          </div>
                        </div>
                        <AdminMetadataText className="text-offblack mb-2 block">
                          {location.location}
                        </AdminMetadataText>
                        <div className="flex items-center gap-1 mb-3">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg
                              key={star}
                              className={`w-4 h-4 ${star <= Math.floor(location.rating) ? "text-warmyellow" : "text-lightgray"}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                          <AdminMetric size="small" className="text-offblack ml-1">
                            {location.rating}
                          </AdminMetric>
                        </div>
                        <AdminHeaderAction
                          type="button"
                          className="!w-full"
                          onClick={() => handleViewProfile(location.id)}
                        >
                          View profile
                        </AdminHeaderAction>
                        
                        {/* Arrow pointing down */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </GoogleMap>
          ) : loadError ? (
            <div className="w-full h-full min-h-[300px] bg-mistygray border border-lightgray flex items-center justify-center">
              <div className="text-center p-4">
                <AdminAlertText className="text-alertred mb-2">Google Maps Error</AdminAlertText>
                <AdminMainText className="text-mediumgray block">
                  Failed to load Google Maps API
                </AdminMainText>
                <AdminMetadataText className="text-mediumgray mt-2 block">
                  Check your API key configuration
                </AdminMetadataText>
              </div>
            </div>
          ) : (
            <div className="w-full h-full min-h-[300px] bg-mistygray border border-lightgray flex items-center justify-center">
              <AdminMainText className="text-mediumgray">Loading Google Maps...</AdminMainText>
            </div>
          )}

          <div className="absolute bottom-4 px-4 w-full flex flex-row justify-between">
            <AdminFormSecondaryAction type="button" className="!w-auto shadow-md" onClick={focusOnMySuburb}>
              Focus on my suburb
            </AdminFormSecondaryAction>
            <AdminFormSecondaryAction type="button" className="!w-auto shadow-md" onClick={resetView}>
              Reset view
            </AdminFormSecondaryAction>
            <div className="w-[100px]"></div>
          </div>
        </div>
        
        {/* Right Section: Trending Services */}
        <div className="w-44 border-l border-mediumgray p-2.5">
          <AdminSupportingText className="text-offblack mb-4 border-b border-lightgray pb-2 block font-medium">
            Trending Services Near You
          </AdminSupportingText>
          <div className="space-y-3">
            {trendingServices.map((service, index) => (
              <React.Fragment key={index}>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-start">
                    <AdminMainText className="text-offblack leading-tight">{service.name}</AdminMainText>
                    <AdminMetadataText className="text-freshgreen whitespace-nowrap ml-2">
                      +{service.change}% ▲
                    </AdminMetadataText>
                  </div>
                  <AdminMetadataText className="text-mediumgray block">
                    {getDemandLabel(service.change)}
                  </AdminMetadataText>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-lightgray rounded-full h-[5px] overflow-hidden">
                      <div
                        className="bg-primaryblue h-[5px] rounded-full transition-all duration-300"
                        style={{ width: `${(service.avgViews / maxViews) * 100}%` }}
                        title={`Average Views (${mapFilterPeriod}): ${service.avgViews}`}
                      />
                    </div>
                    <AdminMetric size="small" className="text-offblack min-w-[35px] text-right">
                      {service.avgViews}
                    </AdminMetric>
                  </div>
                </div>
                {index !== trendingServices.length - 1 && (
                  <div className="w-full h-[1px] bg-lightgray"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default LocalTradesTrendingServicesWidget;
